import { MouseEventHandler } from 'react';
import { useAlert } from 'react-alert';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { takeUpOnOffer } from '@lootboy/lib/actions/OfferActions';
import { IOffer } from '@lootboy/lib/types/offers';

import Alert from '~/components/Alert';
import Image from '~/components/Image';
import NewTabOpener from '~/components/NewTabOpener';

import useBooleanState from '~/hooks/useBooleanState';
import { useRedirectToLogin } from '~/hooks/useRedirectToLogin';
import { useAppDispatch } from '~/redux/store/useAppDispatch';

import diamond from '~/assets/images/currencies/Diamond Front.png';
import { ReactComponent as CheckIcon } from '~/assets/images/iconsLibrary/check-circle.svg';
import { ReactComponent as LinkIcon } from '~/assets/images/iconsLibrary/external-link.svg';

import styles from './QuestTile.module.scss';

function QuestTileContent({ quest }: { quest: IOffer }) {
  return (
    <>
      <div className={styles.reward}>
        {quest.diamondBonus ?? 0}
        <Image
          alt="Lootgem"
          src={diamond}
          className={styles.diamond}
        />
      </div>
      <p className={styles.description}>
        {quest.completed
          ? I18n.t('QuestWallView_offer_completed')
          : quest.description}
      </p>
      {quest.completed ? (
        <CheckIcon className={styles.checkIcon} />
      ) : (
        <LinkIcon className={styles.linkIcon} />
      )}
    </>
  );
}

export function QuestTile({
  quest,
  isLoggedIn,
}: {
  quest: IOffer;
  isLoggedIn: boolean;
}): JSX.Element {
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const redirectToLogin = useRedirectToLogin();

  const [
    isProcessing,
    { setTrue: setProcessingStared, setFalse: setProcessingFinished },
  ] = useBooleanState();

  if (quest.completed) {
    return (
      <div className={classNames(styles.container, styles.completed)}>
        <QuestTileContent quest={quest} />
      </div>
    );
  }

  const onQuestClick: MouseEventHandler = async (e) => {
    if (isProcessing) {
      e.preventDefault();
      return;
    }
    if (!isLoggedIn) {
      e.preventDefault();
      redirectToLogin();
      return;
    }
    setProcessingStared();

    try {
      await dispatch(takeUpOnOffer(quest._id));
    } catch (error) {
      alert.error(
        <Alert
          body={(error as { message: string }).message}
          title={I18n.t('common_error')}
        />,
      );
    } finally {
      setProcessingFinished();
    }
  };

  return (
    <div className={styles.container}>
      <NewTabOpener
        className={styles.link}
        href={quest.url}
        onClick={onQuestClick}
      >
        <QuestTileContent quest={quest} />
      </NewTabOpener>
    </div>
  );
}
